import { useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import BackButton from '../../../../components/ui-components/BackButton'
import { getTopPadding } from '../../../../utils/sessionStorage'
import { lightImpactVibrate } from '../../../../utils/appBridge'
import {
  TaxHarvestingScreenName,
  TaxHarvestingEventId,
  TaxHarvestingButtonLabel,
  trackTaxHarvestingButtonTap,
  trackTaxHarvestingUserClicked
} from '../../tracking/taxHarvestingTracking'
import GainsHarvestingPlan from '../../components/plan/GainsHarvestingPlan'
import TaxSavingsPlan from '../../components/plan/TaxSavingsPlan'
import TaxHarvestingFooter from '../../components/summary/TaxHarvestingFooter'
import { fetchTaxHarvestingSummary } from '../../api/taxHarvestingSummary'
import type { TaxHarvestingSummaryResponse } from '../../types/summary.types'
import { HarvestingPlanType } from '../../types/summary.types'
import type { TaxHarvestingPlanDetailResponse } from '../../api/planDetailApi'
import { fetchTaxHarvestingPlanDetail } from '../../api/planDetailApi'
import {
  LtcgExemptionInfoSheet,
  EquityLtcgTaxLiability,
  NonEquityLtcgTaxLiability
} from '../../bottom-sheets/LtcgBottomSheet'
import { EquityStcgTaxLiability, NonEquityStcgTaxLiability, RealisedStclInfoSheet } from '../../bottom-sheets/StcgBottomSheet'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { PlanTabs } from '../../components/plan/PlanTabs'
import ThreeLineLoading from '../../../../components/ui-components/loading/ThreeLineLoading'
import { TwoLineSkeleton } from '../../components/common/TwoLineSkeleton'
import { formatFyDisplay } from '../../utils/misc'

export default function TaxHarvestingPlanPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const harvestTypeParam = searchParams.get('harvestType') ?? ''
  const gainTypeParam = searchParams.get('gainType') ?? ''
  const harvestType = Object.values(HarvestingPlanType).includes(harvestTypeParam as HarvestingPlanType)
    ? (harvestTypeParam as HarvestingPlanType)
    : null

  const { data: summary, isLoading: isSummaryLoading, isError: isSummaryError } =
    useQuery<TaxHarvestingSummaryResponse>({
      queryKey: ['taxHarvestingSummary'],
      queryFn: fetchTaxHarvestingSummary
    })

  const initialTab: 'EQUITY' | 'NON_EQUITY' =
    harvestTypeParam.startsWith('EQ_') ? 'EQUITY' : 'NON_EQUITY'
  const [activeTab, setActiveTab] = useState<'EQUITY' | 'NON_EQUITY'>(initialTab)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const isStcgHarvestType =
    harvestType === HarvestingPlanType.EQ_STCG ||
    harvestType === HarvestingPlanType.EQ_STCL ||
    harvestType === HarvestingPlanType.NEQ_STCG ||
    harvestType === HarvestingPlanType.NEQ_STCL

  const effectiveHarvestTypeForPlanDetail: HarvestingPlanType = isStcgHarvestType
    ? activeTab === 'EQUITY'
      ? HarvestingPlanType.EQ_STCG
      : HarvestingPlanType.NEQ_STCG
    : activeTab === 'EQUITY'
      ? HarvestingPlanType.EQ_LTCG
      : HarvestingPlanType.NEQ_LTCG

  const {
    data: planDetail,
    isLoading: isPlanLoading,
    isError: isPlanError
  } = useQuery<TaxHarvestingPlanDetailResponse>({
    queryKey: ['taxHarvestingPlanDetail', effectiveHarvestTypeForPlanDetail],
    queryFn: () => fetchTaxHarvestingPlanDetail(effectiveHarvestTypeForPlanDetail),
    enabled: Boolean(harvestType)
  })

  const handleBack = () => {
    trackTaxHarvestingButtonTap({
      screen: TaxHarvestingScreenName.Plan,
      eventId: TaxHarvestingEventId.Back,
      buttonLabel: TaxHarvestingButtonLabel.Back
    })
    lightImpactVibrate()
    navigate(-1)
  }

  const totalExemption = summary?.totalExemption ?? 0
  const usedExemption = summary?.usedExemption ?? 0

  // From taxHarvestingSummary: find the EQ_LTCG plan and check which harvesting types are actually possible
  const eqLtcgPlanFromSummary = summary?.ltcgFinancialPlanResponses?.find(
    (plan) => plan.folioTaxHarvestingType === HarvestingPlanType.EQ_LTCG
  )
  const nonEqLtcgPlanFromSummary = summary?.ltcgFinancialPlanResponses?.find(
    (plan) => plan.folioTaxHarvestingType === HarvestingPlanType.NEQ_LTCG
  )
  const planForHarvestType = isStcgHarvestType
    ? summary?.stcgFinancialPlanResponses?.find((p) => p.folioTaxHarvestingType === effectiveHarvestTypeForPlanDetail)
    : summary?.ltcgFinancialPlanResponses?.find((p) => p.folioTaxHarvestingType === effectiveHarvestTypeForPlanDetail)
  const possibleFolioTaxHarvestingTypesForPlan = planForHarvestType?.possibleFolioTaxHarvestingTypes
  const possibleFolioTaxHarvestingTypes = eqLtcgPlanFromSummary?.possibleFolioTaxHarvestingTypes
  const canShowGainsPlanForLtcg =
    harvestType === HarvestingPlanType.EQ_LTCG &&
    Array.isArray(possibleFolioTaxHarvestingTypes) &&
    possibleFolioTaxHarvestingTypes.includes(HarvestingPlanType.EQ_LTCG)

  const isUserElite = summary?.isUserElite ?? false
  const showEliteLockOverlay = !isUserElite

  const isInitialLoading = isSummaryLoading && !summary
  const isError = isSummaryError || isPlanError || !summary

  const handleTabChange = (tab: 'EQUITY' | 'NON_EQUITY') => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Plan,
      eventId: TaxHarvestingEventId.PlanTab,
      buttonLabel: tab
    })
    setActiveTab(tab)
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }

  const fyHeaderLabel = useMemo(
    () =>
      formatFyDisplay({
        format: 'short',
        currentFyStart: summary?.currentFyStart,
        currentFyEnd: summary?.currentFyEnd,
        currentFy: summary?.currentFy
      }),
    [summary?.currentFyStart, summary?.currentFyEnd, summary?.currentFy]
  )

  const renderTopTaxLiabilitySection = () => {
    if (isStcgHarvestType) {
      if (activeTab === 'EQUITY') {
        return (
          <EquityStcgTaxLiability
            summary={summary}
            showPlanHighlights
            equityPlanPossibleTaxSave={planForHarvestType?.possibleTaxSave}
            shortTermGainsSummary={summary?.shortTermGainsSummary}
          />
        )
      }

      return <NonEquityStcgTaxLiability summary={summary} showPlanHighlights shortTermGainsSummary={summary?.shortTermGainsSummary} />
    }

    if (activeTab === 'EQUITY') {
      return (
        <EquityLtcgTaxLiability
          summary={summary}
          onExemptionClick={() => {
            trackTaxHarvestingUserClicked({
              screen: TaxHarvestingScreenName.Plan,
              eventId: TaxHarvestingEventId.ExemptionUsedInfo
            })
            openBottomSheet(
              <LtcgExemptionInfoSheet
                totalExemption={totalExemption}
                usedExemption={usedExemption}
                onDismiss={closeBottomSheet}
              />,
              closeBottomSheet
            )
          }}
          onRealisedStclClick={() => {
            trackTaxHarvestingUserClicked({
              screen: TaxHarvestingScreenName.Plan,
              eventId: TaxHarvestingEventId.RealisedStclInfo
            })
            openBottomSheet(<RealisedStclInfoSheet onDismiss={closeBottomSheet} />, closeBottomSheet)
          }}
          showPlanHighlights
          hasEquityLtcgPlan={Boolean(eqLtcgPlanFromSummary)}
          equityPlanPossibleTaxSave={eqLtcgPlanFromSummary?.possibleTaxSave}
          equityPlanTaxFreeBookAmount={eqLtcgPlanFromSummary?.taxFreeBookAmount}
          equityPlanGainBookAmount={eqLtcgPlanFromSummary?.gainBookAmount}
          longTermGainsSummary={summary?.longTermGainsSummary}
        />
      )
    }

    return (
      <NonEquityLtcgTaxLiability
        summary={summary}
        onRealisedStclClick={() => {
          trackTaxHarvestingUserClicked({
            screen: TaxHarvestingScreenName.Plan,
            eventId: TaxHarvestingEventId.RealisedStclInfo
          })
          openBottomSheet(<RealisedStclInfoSheet onDismiss={closeBottomSheet} />, closeBottomSheet)
        }}
        showPlanHighlights
        nonEquityPlanPossibleTaxSave={nonEqLtcgPlanFromSummary?.possibleTaxSave}
        longTermGainsSummary={summary?.longTermGainsSummary}
      />
    )
  }

  const renderPlanSection = () => {
    if (isPlanLoading && !planDetail) {
      return (
        <div className='px-5 py-6'>
          <TwoLineSkeleton />
        </div>
      )
    }

    if (isStcgHarvestType && planDetail?.planAvailable) {
      return (
        <TaxSavingsPlan
          showEliteLockOverlay={showEliteLockOverlay}
          harvestType={effectiveHarvestTypeForPlanDetail}
          totalFundCount={planDetail?.totalFundCount}
          eligiblePlanDetails={planDetail?.eligiblePlanDetails}
          remainingPlanDetails={planDetail?.remainingPlanDetails}
          summaryPossibleTaxSave={planForHarvestType?.possibleTaxSave}
          summaryGainBookAmount={planForHarvestType?.gainBookAmount}
          possibleFolioTaxHarvestingTypes={possibleFolioTaxHarvestingTypesForPlan}
        />
      )
    }

    const canRenderTaxSavingsPlan = Boolean(planDetail?.planAvailable)

    if (activeTab === 'EQUITY') {
      if (canShowGainsPlanForLtcg) {
        return (
          <GainsHarvestingPlan
            showEliteLockOverlay={showEliteLockOverlay}
            totalFundCount={planDetail?.totalFundCount}
            eligiblePlanDetails={planDetail?.eligiblePlanDetails}
            remainingPlanDetails={planDetail?.remainingPlanDetails}
            harvestType={effectiveHarvestTypeForPlanDetail}
            possibleFolioTaxHarvestingTypes={possibleFolioTaxHarvestingTypesForPlan}
            summaryPossibleTaxSave={eqLtcgPlanFromSummary?.possibleTaxSave}
            summaryTaxFreeBookAmount={eqLtcgPlanFromSummary?.taxFreeBookAmount}
          />
        )
      }

      if (canRenderTaxSavingsPlan) {
        return (
          <TaxSavingsPlan
            showEliteLockOverlay={showEliteLockOverlay}
            harvestType={effectiveHarvestTypeForPlanDetail}
            totalFundCount={planDetail?.totalFundCount}
            eligiblePlanDetails={planDetail?.eligiblePlanDetails}
            remainingPlanDetails={planDetail?.remainingPlanDetails}
            summaryPossibleTaxSave={planForHarvestType?.possibleTaxSave}
            summaryGainBookAmount={planForHarvestType?.gainBookAmount}
            possibleFolioTaxHarvestingTypes={possibleFolioTaxHarvestingTypesForPlan}
          />
        )
      }

      return null
    }

    if (canRenderTaxSavingsPlan) {
      return (
        <TaxSavingsPlan
          showEliteLockOverlay={showEliteLockOverlay}
          harvestType={effectiveHarvestTypeForPlanDetail}
          totalFundCount={planDetail?.totalFundCount}
          eligiblePlanDetails={planDetail?.eligiblePlanDetails}
          remainingPlanDetails={planDetail?.remainingPlanDetails}
          summaryPossibleTaxSave={planForHarvestType?.possibleTaxSave}
          summaryGainBookAmount={planForHarvestType?.gainBookAmount}
          possibleFolioTaxHarvestingTypes={possibleFolioTaxHarvestingTypesForPlan}
        />
      )
    }

    return null
  }

  const canShowAnyPlanOnCurrentTab = useMemo(() => {
    const hasPlanAvailable = !!planDetail?.planAvailable

    if (isStcgHarvestType) {
      return hasPlanAvailable
    }

    if (activeTab === 'EQUITY') {
      return canShowGainsPlanForLtcg || hasPlanAvailable
    }

    return hasPlanAvailable
  }, [planDetail?.planAvailable, isStcgHarvestType, activeTab, canShowGainsPlanForLtcg])

  if (isInitialLoading) {
    return (
      <ThreeLineLoading />
    )
  }

  if (isError) {
    return (
      <div className='h-screen bg-white flex flex-col items-center justify-center px-5'>
        <p className='text-[14px] font-[400] text-[#6A6E6E]'>
          Something went wrong while loading your tax plan. Please try again.
        </p>
      </div>
    )
  }

  return (
    <div className='h-screen bg-white flex flex-col'>
      <div
        style={{ paddingTop: `calc(${getTopPadding()}px + 8px)` }}
        className='shrink-0 pb-0'
      >
        <div className='pl-[19px] pr-5 pb-5 flex items-center'>
          <div>
            <BackButton size={24} onClick={handleBack} />
          </div>
          {gainTypeParam && (
            <div className='text-[#232323] text-[16px] font-[500] pl-4'>
              {gainTypeParam} Summary
            </div>
          )}
          {fyHeaderLabel && (
            <div className='text-[#8E9393] text-[16px] font-[400] pl-1'>
              / {fyHeaderLabel}
            </div>
          )}
        </div>

        <PlanTabs activeTab={activeTab} onChange={handleTabChange} />

      </div>

      <div ref={scrollContainerRef} className='flex-1 min-h-0 overflow-y-auto no-scrollbar'>
        <div className='px-5 pb-6'>
          <div className='mt-6 flex flex-col gap-4'>
            {renderTopTaxLiabilitySection()}
          </div>
        </div>
        <div className='bg-grey80 w-full h-[12px]' />

        {/* Middle section: Gains harvesting / Tax savings plan list based on tab.
            When switching tabs, keep the page shell visible and show a skeleton only for this section. */}
        {renderPlanSection()}

        {/* Bottom section: Footer with disclaimer, taxation rules, FAQs */}
        <TaxHarvestingFooter showFloatingButtonIfUserIsNotElite={canShowAnyPlanOnCurrentTab} />
      </div>
    </div>
  )
}

